// Query taxonomy engine - classifies intent and determines system behavior per query type

export type QueryClass =
  | "product_discovery"    // "best X under Y"
  | "replacement"          // "replacement for X"
  | "urgency"              // "need X by tomorrow"
  | "compatibility"        // "X compatible with Y"
  | "gift"                 // "gift for X"
  | "procurement"          // bulk / business buying
  | "replenishment"        // recurring purchase
  | "price_watch"          // "is X a good price"
  | "product_audit"        // paste link, check if worth it
  | "should_i_buy"         // "should I buy X"
  | "better_than"          // "is X better than Y"
  | "general";             // fallback

export interface QueryBehavior {
  clarificationStyle: "minimal" | "standard" | "detailed";
  rankingStyle: "balanced" | "price_first" | "speed_first" | "trust_first" | "quality_first";
  outputStyle: "decisive" | "comparative" | "advisory";
  ctaStyle: "buy_now" | "track" | "wait" | "compare";
  confidenceWeight: number; // 0-1, how much confidence matters for this query type
  maxClarifications: number;
}

const QUERY_BEHAVIORS: Record<QueryClass, QueryBehavior> = {
  product_discovery: {
    clarificationStyle: "standard",
    rankingStyle: "balanced",
    outputStyle: "decisive",
    ctaStyle: "buy_now",
    confidenceWeight: 0.8,
    maxClarifications: 2,
  },
  replacement: {
    clarificationStyle: "minimal",
    rankingStyle: "quality_first",
    outputStyle: "decisive",
    ctaStyle: "buy_now",
    confidenceWeight: 0.9,
    maxClarifications: 1,
  },
  urgency: {
    clarificationStyle: "minimal",
    rankingStyle: "speed_first",
    outputStyle: "decisive",
    ctaStyle: "buy_now",
    confidenceWeight: 0.7,
    maxClarifications: 0,
  },
  compatibility: {
    clarificationStyle: "detailed",
    rankingStyle: "trust_first",
    outputStyle: "advisory",
    ctaStyle: "compare",
    confidenceWeight: 0.95,
    maxClarifications: 2,
  },
  gift: {
    clarificationStyle: "standard",
    rankingStyle: "balanced",
    outputStyle: "decisive",
    ctaStyle: "buy_now",
    confidenceWeight: 0.7,
    maxClarifications: 2,
  },
  procurement: {
    clarificationStyle: "detailed",
    rankingStyle: "price_first",
    outputStyle: "comparative",
    ctaStyle: "compare",
    confidenceWeight: 0.85,
    maxClarifications: 2,
  },
  replenishment: {
    clarificationStyle: "minimal",
    rankingStyle: "price_first",
    outputStyle: "decisive",
    ctaStyle: "buy_now",
    confidenceWeight: 0.6,
    maxClarifications: 0,
  },
  price_watch: {
    clarificationStyle: "minimal",
    rankingStyle: "price_first",
    outputStyle: "advisory",
    ctaStyle: "track",
    confidenceWeight: 0.5,
    maxClarifications: 0,
  },
  product_audit: {
    clarificationStyle: "minimal",
    rankingStyle: "trust_first",
    outputStyle: "advisory",
    ctaStyle: "compare",
    confidenceWeight: 0.9,
    maxClarifications: 0,
  },
  should_i_buy: {
    clarificationStyle: "standard",
    rankingStyle: "balanced",
    outputStyle: "advisory",
    ctaStyle: "compare",
    confidenceWeight: 0.85,
    maxClarifications: 1,
  },
  better_than: {
    clarificationStyle: "minimal",
    rankingStyle: "quality_first",
    outputStyle: "comparative",
    ctaStyle: "compare",
    confidenceWeight: 0.8,
    maxClarifications: 1,
  },
  general: {
    clarificationStyle: "standard",
    rankingStyle: "balanced",
    outputStyle: "decisive",
    ctaStyle: "buy_now",
    confidenceWeight: 0.7,
    maxClarifications: 2,
  },
};

const CLASS_PATTERNS: { class: QueryClass; patterns: RegExp[] }[] = [
  { class: "urgency", patterns: [/need.*(today|tonight|tomorrow|asap|urgent|right now|by \w+day)/i, /deliver.*(today|tonight|tomorrow)/i] },
  { class: "replacement", patterns: [/replace(ment)?\s+(for|of|my)/i, /broke|broken|died|dead|stopped working/i, /upgrade from/i] },
  { class: "compatibility", patterns: [/compatible with/i, /work with/i, /fit (my|the|a)/i, /match(es|ing)?\s/i] },
  { class: "gift", patterns: [/gift\s+(for|idea)/i, /present\s+for/i, /birthday|christmas|anniversary/i] },
  { class: "procurement", patterns: [/bulk|wholesale|office\s+order/i, /\d{2,}\s+(units?|pieces?|packs?)/i] },
  { class: "replenishment", patterns: [/reorder|restock|run(ning)?\s+out/i, /need more/i, /same (as|one) (last|before)/i] },
  { class: "price_watch", patterns: [/good (price|deal)/i, /worth (it|buying|the price)/i, /price drop|on sale/i, /is \$?\d+ (good|fair|too much)/i] },
  { class: "product_audit", patterns: [/^https?:\/\//i, /check (this|a) (link|product|listing)/i] },
  { class: "should_i_buy", patterns: [/should I (buy|get|order)/i, /is (it|this) worth/i] },
  { class: "better_than", patterns: [/better than/i, /vs\.?\s/i, /or\s+(the|a)?\s*\w+\s*\?/i, /compared? to/i] },
  { class: "product_discovery", patterns: [/^best\s/i, /^top\s/i, /^find\s/i, /recommend/i, /under \$?\d/i] },
];

export function classifyQuery(query: string): QueryClass {
  for (const { class: cls, patterns } of CLASS_PATTERNS) {
    if (patterns.some(p => p.test(query))) return cls;
  }
  return "general";
}

export function getQueryBehavior(queryClass: QueryClass): QueryBehavior {
  return QUERY_BEHAVIORS[queryClass];
}

export function getOutputVerdict(queryClass: QueryClass, confidence: string): string {
  if (queryClass === "urgency") {
    return confidence === "high" ? "Buy now" : "Fastest available option";
  }
  if (queryClass === "price_watch") {
    return "Track this price";
  }
  if (queryClass === "should_i_buy" || queryClass === "product_audit") {
    return confidence === "high" ? "Worth buying" : "Consider alternatives";
  }
  if (queryClass === "better_than") {
    return "See comparison";
  }
  if (queryClass === "replenishment") {
    return "Reorder now";
  }
  return confidence === "high" ? "Buy now" : "Best available option";
}
